import { useState, useEffect, Suspense, lazy } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  TextField,
  Typography,
  Chip,
  Tooltip
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { useHistory } from 'react-router-dom';
import {
  CreateOrUpdateCarrier,
  GetAgencyLocations,
  GetCarrierLocations,
  UploadCarrierLogo
} from '../../apis/Agency';
import { ErrorToast, successToast } from '../../components/Toasts';
import { ToastContainer } from 'react-toastify';
import { useSelector } from 'react-redux';
import { base_logo_url } from '../../apis/constants';
import AddLocations from '../Locations/AddLocations';

// Icons
import UploadIcon from '@mui/icons-material/Upload';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import AddIcon from '@mui/icons-material/Add';

const AddressAutocomplete = lazy(() => import('./AddressAutocomplete'));

const CARRIER_TYPES = [
  'Bulk Tanker',
  'Dry Van 53ft',
  'Reefer',
  'Box Truck',
  'Sprinter',
  'Intermodal'
];

const icon = <CheckBoxOutlineBlankIcon fontSize='small' />;
const checkedIcon = <CheckBoxIcon fontSize='small' />;

function SectionHeader({ icon: Icon, title }) {
  return (
    <Box
      sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, mt: 0.5 }}
    >
      <Icon sx={{ fontSize: 15, color: 'text.secondary' }} />
      <Typography
        variant='caption'
        sx={{
          fontWeight: 600,
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: '0.07em',
          color: 'text.secondary'
        }}
      >
        {title}
      </Typography>
      <Box sx={{ flex: 1, height: '0.5px', bgcolor: 'divider' }} />
    </Box>
  );
}

function FieldLabel({ label, required }) {
  return (
    <Typography
      variant='caption'
      sx={{
        fontWeight: 500,
        fontSize: 11.5,
        color: 'text.secondary',
        mb: 0.4,
        display: 'block'
      }}
    >
      {label}
      {required && <span style={{ color: '#D4537E', marginLeft: 2 }}>*</span>}
    </Typography>
  );
}

export default function CreateCarrier(props) {
  const AgencyID = useSelector(state => state.userDetails.agencyLoginID);
  const UserIsAdmin = useSelector(state => state.userDetails.isAdmin);
  const userRolePermissions = useSelector(
    state => state.userDetails.userRolePermissionsArr
  );

  const isNewUI = props?.action === 'NewUI';
  const EditedData = isNewUI ? props?.state : props?.location?.state;
  const history = useHistory();
  const isEdit = Boolean(EditedData?.carrierId);

  const [carrierName, setCarrierName] = useState(EditedData?.name ?? '');
  const [status, setStatus] = useState(
    EditedData?.active === false ? 'Inactive' : 'Active'
  );
  const [carrierType, setCarrierType] = useState(
    EditedData?.cSubTypes ??
      (EditedData?.quickQuoteData?.equipmentType
        ? [EditedData.quickQuoteData.equipmentType]
        : [])
  );
  const [agencyImage, setAgencyImage] = useState(EditedData?.logo ?? null);
  const [agencyApiUploadedImage, setAgencyApiUploadedImage] = useState(
    EditedData?.logo ?? ''
  );
  const [uploadError, setUploadError] = useState('');
  const [address1, setAddress1] = useState(EditedData?.streetAddress1 ?? '');
  const [address2, setAddress2] = useState(EditedData?.streetAddress2 ?? '');
  const [zip, setZip] = useState(EditedData?.postalCode ?? '');
  const [city, setCity] = useState(EditedData?.city ?? '');
  const [state, setState] = useState(EditedData?.state ?? '');
  const [country, setCountry] = useState(EditedData?.country ?? '');
  const [shipFromFinalValue, setShipFromFinalValue] = useState(
    EditedData?.streetAddress1 ?? ''
  );
  const [firstName, setFirstName] = useState(
    EditedData?.contactFirstName ?? ''
  );
  const [lastName, setLastName] = useState(EditedData?.contactLastName ?? '');
  const [email, setEmail] = useState(EditedData?.contactEmail ?? '');
  const [phoneNumber, setPhoneNumber] = useState(
    EditedData?.contactPhone ?? ''
  );
  const [designation, setDesignation] = useState(
    EditedData?.contactDesignation ?? ''
  );
  const [userLocations, setUserLocations] = useState([]);
  const [agencyLocations, setAgencyLocations] = useState([]);
  const [agencyLocationsLoader, setAgencyLocationsLoader] = useState(false);
  const [createLocDialog, setCreateLocDialog] = useState(false);
  const [createLocPermission, setCreateLocPermission] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [isValidEmail, setIsValidEmail] = useState(true);
  const [phoneNumberValidError, setPhoneNumberValidError] = useState(false);
  const [carriersLoader, setCarriersLoader] = useState(false);

  useEffect(() => {
    const perm = userRolePermissions?.filter(
      i => i.permissionName === 'Locations'
    );
    setCreateLocPermission(perm);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    GetAgencyLocationsHandler();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const GetAgencyLocationsHandler = () => {
    setAgencyLocationsLoader(true);
    GetAgencyLocations(AgencyID, 0)
      .then(res => {
        if (res.status === 200) {
          setAgencyLocationsLoader(false);
          const filtered = res.data.data.map(
            ({ locationCarriers, ...rest }) => rest
          );
          setAgencyLocations(filtered);
          if (EditedData?.carrierId) handleAssignedLocations(res.data.data);
        }
      })
      .catch(err => console.log(err.message));
  };

  const handleAssignedLocations = allLocs => {
    GetCarrierLocations(AgencyID, EditedData.carrierId)
      .then(res => {
        if (res.status === 200) {
          const assigned = res.data.data.locationIds
            .map(id => allLocs.find(l => l.id === id))
            .filter(Boolean);
          setUserLocations(assigned);
        }
      })
      .catch(err => console.log(err));
  };

  const handleAgencyImageUpload = event => {
    const file = event.target.files[0];
    if (!file) {
      ErrorToast('No file selected');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setUploadError('File size must be under 2 MB');
      return;
    }
    setUploadError('');
    const fd = new FormData();
    fd.append('file', file);
    UploadCarrierLogo(AgencyID, fd)
      .then(res => {
        if (res.status === 200 && res.data?.data) {
          setAgencyApiUploadedImage(res.data.data);
          setAgencyImage(res.data.data);
        }
      })
      .catch(err => ErrorToast(err?.message || 'Upload failed'));
  };

  const handlePhoneNumberChange = e => {
    const digits = e.target.value.replace(/\D/g, '');
    let formatted = digits;
    if (digits.length >= 7)
      formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    else if (digits.length >= 4)
      formatted = `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    else if (digits.length > 0) formatted = `(${digits}`;
    setPhoneNumber(formatted);
    const valid = /^\(\d{3}\) \d{3}-\d{4}$/.test(formatted) || formatted === '';
    setPhoneNumberValidError(!valid && formatted.length > 0);
    setValidationErrors(prev => ({
      ...prev,
      phoneNumberError: valid ? null : 'Format: (XXX) XXX-XXXX'
    }));
  };

  const handleEmailValidation = val => {
    const regex =
      /^([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})(,[\s]*[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})*$/;
    setEmail(val);
    const valid = regex.test(val) || val === '';
    setIsValidEmail(valid);
    setValidationErrors(prev => ({
      ...prev,
      emailError: valid ? null : 'Separate multiple emails with a comma'
    }));
  };

  const toggleCarrierType = type => {
    setCarrierType(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
    setValidationErrors(prev => ({ ...prev, carrierType: null }));
  };

  const handleLocationChange = (event, newValue) => {
    const hasSelectAll = newValue.some(l => l.selectAll);
    if (hasSelectAll) {
      setUserLocations(
        userLocations.length === agencyLocations.length ? [] : agencyLocations
      );
    } else {
      setUserLocations(newValue.filter(l => !l.selectAll && !l.isButton));
    }
    setValidationErrors(prev => ({ ...prev, locationsError: null }));
  };

  const removeLocation = loc =>
    setUserLocations(prev => prev.filter(l => l.id !== loc.id));

  const validateForm = () => {
    const errors = {};
    if (!carrierName) errors.carrierNameError = 'Enter carrier name';
    if (!carrierType.length)
      errors.carrierType = 'Select at least one carrier type';
    if (!firstName) errors.firstNameError = 'Enter first name';
    if (!lastName) errors.lastNameError = 'Enter last name';
    if (!email) errors.emailError = 'Enter email';
    else if (!isValidEmail) errors.emailError = 'Enter a valid email';
    if (!phoneNumber) errors.phoneNumberError = 'Enter phone number';
    else if (phoneNumberValidError)
      errors.phoneNumberError = 'Format: (XXX) XXX-XXXX';
    if (!address1) errors.address1Error = 'Enter address';
    if (!userLocations.length)
      errors.locationsError = 'Assign at least one location';
    setValidationErrors(errors);
    return !Object.keys(errors).length;
  };

  const handleSubmitCarrier = () => {
    if (!validateForm()) return;
    const payload = {
      agencyId: AgencyID,
      id: EditedData?.carrierId ?? 0,
      name: carrierName,
      icon: agencyApiUploadedImage,
      url: '',
      cType: '',
      cSubType: '',
      active: status === 'Active',
      firstName,
      lastName,
      email,
      phone: phoneNumber,
      streetAddress1: address1,
      streetAddress2: address2,
      postalCode: zip,
      city,
      country,
      state,
      locationIds: userLocations.map(o => o.id),
      designation,
      cSubTypes: carrierType
    };
    setCarriersLoader(true);
    CreateOrUpdateCarrier(payload)
      .then(() => {
        successToast(isEdit ? 'Carrier updated!' : 'Carrier created!');
        setTimeout(() => {
          if (isNewUI) {
            props?.fetchCarriers();
            props?.handleCloseTLView();
          } else history.push('/carriers', 'Non-LTL');
        }, 2000);
        setCarriersLoader(false);
      })
      .catch(err => {
        ErrorToast(err?.response?.data?.reasonPhrase || err?.message);
        setCarriersLoader(false);
      });
  };

  const goBack = () => history.push('/carriers', 'Non-LTL');
  const formatLocation = opt => {
    if (!opt || opt.selectAll) return opt?.selectAll ? 'Select all' : '';
    if (opt.isButton) return '';

    const addressParts = [
      opt.city,
      opt.state || opt.stateOrProvinceCode,
      opt.postalCode
    ]
      .filter(Boolean)
      .join(', ');

    return (
      <>
        <b>{opt.name}:</b> {addressParts}
      </>
    );
  };
  const inputSx = {
    '& .MuiInputBase-root': { fontSize: 13 },
    '& .MuiInputLabel-root': { fontSize: 13 }
  };

  const formHeader = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: isNewUI ? 2.5 : 0,
        py: isNewUI ? 1.5 : 0,
        bgcolor: '#fafafa',
        borderBottom: '0.5px solid',
        borderColor: 'divider'
      }}
    >
      <Typography
        variant='caption'
        sx={{ fontSize: 11, ml: 1, color: 'text.secondary' }}
      >
        <span style={{ color: '#D4537E' }}>*</span> Required fields
      </Typography>
      {/* {isNewUI && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: '#1976d2'
            }}
          />
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: 15, lineHeight: 1.2 }}>
              {isEdit ? 'Edit Carrier' : 'Add New Carrier'}
            </Typography>
            <Typography
              variant='caption'
              sx={{ color: 'text.secondary', fontSize: 11 }}
            >
              {isEdit
                ? 'Update carrier details below'
                : 'Fill in all required fields to register a carrier'}
            </Typography>
          </Box>
        </Box>
      )} */}
      <Tooltip title='Close'>
        <IconButton
          size='small'
          onClick={goBack}
          sx={{ color: 'error.main', '&:hover': { bgcolor: 'error.lighter' } }}
        >
          <CloseIcon fontSize='small' />
        </IconButton>
      </Tooltip>
    </Box>
  );

  const formBody = (
    <Grid container spacing={3}>
      {/* LEFT */}
      <Grid item xs={12} md={6}>
        <SectionHeader
          icon={LocalShippingOutlinedIcon}
          title='Basic information'
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <input
            type='file'
            accept='image/*'
            onChange={handleAgencyImageUpload}
            style={{ display: 'none' }}
            id='carrier-logo-upload'
          />
          <label
            htmlFor='carrier-logo-upload'
            style={{ cursor: 'pointer', flexShrink: 0 }}
          >
            {agencyImage ? (
              <Box
                component='img'
                src={base_logo_url + agencyImage}
                alt='Carrier logo'
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  objectFit: 'cover',
                  border: '1.5px solid',
                  borderColor: 'divider'
                }}
              />
            ) : (
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  border: '1.5px dashed',
                  borderColor: 'divider',
                  bgcolor: '#f5f6f8',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'border-color 0.15s',
                  '&:hover': { borderColor: '#1976d2' }
                }}
              >
                <UploadIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              </Box>
            )}
          </label>
          <Box>
            <Typography
              sx={{ fontSize: 12, fontWeight: 500, color: 'text.primary' }}
            >
              Carrier logo
            </Typography>

            {uploadError && (
              <Typography
                variant='caption'
                color='error'
                sx={{ fontSize: 11, display: 'block' }}
              >
                {uploadError}
              </Typography>
            )}
          </Box>
        </Box>

        <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
          <Grid item xs={8}>
            <TextField
              fullWidth
              size='small'
              variant='standard'
              label='Carrier name'
              required
              placeholder='e.g. Swift Transport'
              value={carrierName}
              onChange={e => {
                setCarrierName(e.target.value);
                setValidationErrors(p => ({ ...p, carrierNameError: null }));
              }}
              error={Boolean(validationErrors.carrierNameError)}
              helperText={validationErrors.carrierNameError}
              sx={inputSx}
            />
          </Grid>
          <Grid item xs={4}>
            <FormControl fullWidth size='small' variant='standard'>
              <InputLabel id='carrier-status-label' sx={{ fontSize: 13 }}>
                Status
              </InputLabel>
              <Select
                labelId='carrier-status-label'
                label='Status'
                value={status}
                onChange={e => setStatus(e.target.value)}
                sx={{ fontSize: 13 }}
              >
                <MenuItem value='Active' sx={{ fontSize: 13 }}>
                  Active
                </MenuItem>
                <MenuItem value='Inactive' sx={{ fontSize: 13 }}>
                  Inactive
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Box sx={{ mb: 2 }}>
          <FieldLabel label='Carrier type' required />
          <Grid container spacing={0.8} sx={{ mt: 0.2 }}>
            {CARRIER_TYPES.map(type => {
              const selected = carrierType.includes(type);
              return (
                <Grid item xs={6} key={type}>
                  <Box
                    onClick={() => toggleCarrierType(type)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1.2,
                      py: 0.8,
                      border: '0.5px solid',
                      borderColor: selected ? '#42a5f5' : 'divider',
                      borderRadius: '8px',
                      bgcolor: selected ? '#e3f2fd' : 'transparent',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      '&:hover': { borderColor: '#42a5f5', bgcolor: '#f0f7ff' }
                    }}
                  >
                    <Checkbox
                      size='small'
                      checked={selected}
                      onChange={() => toggleCarrierType(type)}
                      onClick={e => e.stopPropagation()}
                      sx={{
                        p: 0,
                        color: 'divider',
                        '&.Mui-checked': { color: '#1976d2' }
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: selected ? 500 : 400,
                        color: selected ? '#1565c0' : 'text.primary'
                      }}
                    >
                      {type}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
          {validationErrors.carrierType && (
            <Typography
              variant='caption'
              color='error'
              sx={{ fontSize: 11, mt: 0.5, display: 'block' }}
            >
              {validationErrors.carrierType}
            </Typography>
          )}
        </Box>

        <SectionHeader icon={LocationOnOutlinedIcon} title='Address' />
        <Grid container spacing={1.2}>
          <Grid item xs={12}>
            <Suspense
              fallback={
                <TextField
                  fullWidth
                  size='small'
                  label='Street address'
                  placeholder='Loading…'
                  disabled
                />
              }
            >
              <AddressAutocomplete
                fontSize={12}
                locationFlow='TL'
                errorMsg='address1Error'
                validattionErrors={validationErrors}
                label='Street address *'
                inputVariant='standard'
                size='small'
                defaultValue={shipFromFinalValue}
                onInputChange={val => {
                  setShipFromFinalValue(val);
                  setValidationErrors(p => ({ ...p, address1Error: null }));
                }}
                onSelectDetails={details => {
                  if (!details) {
                    setCity('');
                    setState('');
                    setZip('');
                    setCountry('');
                  } else {
                    setShipFromFinalValue(details.streetLines);
                    setAddress1(details.streetLines);
                    setCity(details.city);
                    setState(details.state);
                    setZip(details.zip);
                    setCountry(details.country);
                  }
                }}
              />
            </Suspense>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size='small'
              variant='standard'
              label='Address 2 (optional)'
              placeholder='e.g. Suite 400'
              value={address2}
              onChange={e => setAddress2(e.target.value)}
              sx={inputSx}
            />
          </Grid>
          <Grid item xs={5}>
            <TextField
              fullWidth
              size='small'
              variant='standard'
              label='City'
              value={city}
              onChange={e => setCity(e.target.value)}
              placeholder='Chicago'
              sx={inputSx}
            />
          </Grid>
          <Grid item xs={3.5}>
            <TextField
              fullWidth
              size='small'
              variant='standard'
              label='State'
              value={state}
              onChange={e => setState(e.target.value)}
              placeholder='IL'
              sx={inputSx}
            />
          </Grid>
          <Grid item xs={3.5}>
            <TextField
              fullWidth
              size='small'
              variant='standard'
              label='ZIP'
              value={zip}
              onChange={e => setZip(e.target.value)}
              placeholder='60601'
              sx={inputSx}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size='small'
              variant='standard'
              label='Country'
              value={country}
              onChange={e => setCountry(e.target.value)}
              placeholder='United States'
              sx={inputSx}
            />
          </Grid>
        </Grid>
      </Grid>

      {/* RIGHT */}
      <Grid item xs={12} md={6}>
        <SectionHeader icon={PersonOutlineIcon} title='Contact person' />
        <Grid container spacing={1.5}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              size='small'
              variant='standard'
              label='First name'
              required
              placeholder='Jane'
              value={firstName}
              onChange={e => {
                setFirstName(e.target.value);
                setValidationErrors(p => ({ ...p, firstNameError: null }));
              }}
              error={Boolean(validationErrors.firstNameError)}
              helperText={validationErrors.firstNameError}
              sx={inputSx}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              size='small'
              variant='standard'
              label='Last name'
              required
              placeholder='Doe'
              value={lastName}
              onChange={e => {
                setLastName(e.target.value);
                setValidationErrors(p => ({ ...p, lastNameError: null }));
              }}
              error={Boolean(validationErrors.lastNameError)}
              helperText={validationErrors.lastNameError}
              sx={inputSx}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size='small'
              variant='standard'
              label='Designation'
              placeholder='e.g. Operations Manager'
              value={designation}
              onChange={e => setDesignation(e.target.value)}
              sx={inputSx}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size='small'
              variant='standard'
              label='Email address'
              required
              type='email'
              placeholder='jane.doe@carrier.com'
              value={email}
              onChange={e => handleEmailValidation(e.target.value)}
              error={Boolean(validationErrors.emailError)}
              helperText={validationErrors.emailError}
              sx={inputSx}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size='small'
              variant='standard'
              label='Phone number'
              required
              placeholder='(312) 555-0190'
              value={phoneNumber}
              onChange={handlePhoneNumberChange}
              error={Boolean(validationErrors.phoneNumberError)}
              helperText={validationErrors.phoneNumberError}
              inputProps={{ maxLength: 14 }}
              sx={inputSx}
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 2.5 }}>
          <SectionHeader
            icon={LocationOnOutlinedIcon}
            title='Warehouse locations'
          />
          {agencyLocationsLoader ? (
            <Skeleton
              variant='rounded'
              height={40}
              sx={{ borderRadius: '8px' }}
            />
          ) : (
            <>
              <Autocomplete
                multiple
                fullWidth
                size='small'
                value={userLocations}
                disableCloseOnSelect
                isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                onChange={handleLocationChange}
                options={[
                  { name: 'Create Location', isButton: true },
                  ...(agencyLocations.length > 1
                    ? [{ name: 'Select All', selectAll: true }]
                    : []),
                  ...agencyLocations
                ]}
                getOptionLabel={opt => formatLocation(opt)}
                renderTags={() => null}
                renderOption={(props, option, { selected }) => {
                  if (option.isButton) {
                    if (
                      createLocPermission?.some(p => p.level3) ||
                      UserIsAdmin
                    ) {
                      return (
                        <li {...props}>
                          <Button
                            size='small'
                            startIcon={<AddIcon fontSize='small' />}
                            onClick={e => {
                              e.stopPropagation();
                              setCreateLocDialog(true);
                            }}
                            sx={{
                              fontSize: 12,
                              color: '#1976d2',
                              textTransform: 'none'
                            }}
                          >
                            Create new location
                          </Button>
                        </li>
                      );
                    }
                    return null;
                  }
                  return (
                    <li {...props}>
                      <Checkbox
                        size='small'
                        icon={icon}
                        checkedIcon={checkedIcon}
                        checked={selected}
                        sx={{
                          p: 0,
                          mr: 1,
                          '&.Mui-checked': { color: '#1976d2' }
                        }}
                      />
                      <Typography sx={{ fontSize: 12 }}>
                        {formatLocation(option)}
                      </Typography>
                    </li>
                  );
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    size='small'
                    variant='standard'
                    placeholder='Search locations…'
                    error={Boolean(validationErrors.locationsError)}
                    helperText={validationErrors.locationsError}
                    sx={inputSx}
                  />
                )}
              />
              {userLocations.length > 0 && (
                <Box
                  sx={{
                    mt: 1.2,
                    p: 1.2,
                    border: '0.5px solid',
                    borderColor: 'divider',
                    borderRadius: '8px',
                    maxHeight: 130,
                    overflowY: 'auto'
                  }}
                >
                  <Typography
                    variant='caption'
                    sx={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: 'text.secondary',
                      mb: 0.8,
                      display: 'block'
                    }}
                  >
                    Assigned ({userLocations.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                    {userLocations.map(loc => (
                      <Chip
                        key={loc.id}
                        // label={`${(<b>{loc.name}</b>)}: ${loc.city}, ${loc.state}, ${loc.postalCode}`}
                        label={
                          <>
                            <strong>{loc.name}</strong>: {loc.city}, {loc.state}
                            , {loc.postalCode}
                          </>
                        }
                        size='small'
                        onDelete={() => removeLocation(loc)}
                        deleteIcon={
                          <DeleteOutlineIcon
                            sx={{ fontSize: '14px !important' }}
                          />
                        }
                        sx={{
                          fontSize: 11,
                          height: 24,
                          bgcolor: '#e3f2fd',
                          color: '#1565c0',
                          border: '0.5px solid #9FE1CB',
                          '& .MuiChip-deleteIcon': {
                            color: '#1565c0',
                            '&:hover': { color: '#085041' }
                          }
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </>
          )}
        </Box>

        <Box
          sx={{
            mt: 2.5,
            p: 1.5,
            bgcolor: '#f5f6f8',
            border: '0.5px solid',
            borderColor: 'divider',
            borderRadius: '10px'
          }}
        >
          <Typography
            variant='caption'
            sx={{
              fontSize: 11,
              fontWeight: 600,
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: '0.06em'
            }}
          >
            Summary
          </Typography>
          <Divider sx={{ my: 0.8 }} />
          {[
            {
              label: 'Status',
              value: (
                <Box
                  component='span'
                  sx={{
                    display: 'inline-block',
                    px: 1,
                    py: 0.2,
                    borderRadius: 10,
                    fontSize: 11,
                    fontWeight: 500,
                    bgcolor: status === 'Active' ? '#e3f2fd' : '#FAEEDA',
                    color: status === 'Active' ? '#1565c0' : '#633806'
                  }}
                >
                  {status}
                </Box>
              )
            },
            {
              label: 'Types',
              value: carrierType.length
                ? carrierType.length <= 2
                  ? carrierType.join(', ')
                  : `${carrierType[0]} +${carrierType.length - 1} more`
                : '—'
            },
            {
              label: 'Locations',
              value: userLocations.length
                ? `${userLocations.length} assigned`
                : '—'
            }
          ].map(({ label, value }) => (
            <Box
              key={label}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 0.6
              }}
            >
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                {label}
              </Typography>
              <Typography
                sx={{ fontSize: 12, fontWeight: 500, color: 'text.primary' }}
              >
                {value}
              </Typography>
            </Box>
          ))}
        </Box>
      </Grid>
    </Grid>
  );

  const formFooter = (
    <Box
      sx={{
        display: 'flex',
        // alignItems: 'end',
        justifyContent: 'end',
        px: 2.5,
        py: 1.5,
        bgcolor: '#fafafa',
        borderTop: '0.5px solid',
        borderColor: 'divider',
        position: 'sticky',
        bottom: 0,
        zIndex: 1
      }}
    >
      <Button
        size='small'
        variant='standard'
        onClick={goBack}
        sx={{
          fontSize: 12,
          mr: 1,
          borderRadius: '8px',
          textTransform: 'none',
          borderColor: 'divider',
          color: 'text.primary',
          '&:hover': { borderColor: '#aaa' }
        }}
      >
        Cancel
      </Button>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Button
          size='small'
          variant='contained'
          onClick={handleSubmitCarrier}
          disabled={carriersLoader}
          sx={{
            minWidth: 130,
            fontSize: 12,
            borderRadius: '8px',
            textTransform: 'none',
            bgcolor: '#1976d2',
            '&:hover': { bgcolor: '#1565c0' },
            boxShadow: 'none'
          }}
        >
          {carriersLoader ? (
            <CircularProgress size={18} sx={{ color: '#fff' }} />
          ) : isEdit ? (
            'Save changes'
          ) : (
            'Create carrier'
          )}
        </Button>
      </Box>
    </Box>
  );

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <>
      <ToastContainer
        position='top-center'
        autoClose={3800}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
      />

      <Dialog
        maxWidth='xl'
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: '90vw', md: '1000px' },
            m: { xs: 1, sm: 2 },
            borderRadius: 2
          }
        }}
        open={createLocDialog}
        onClose={() => setCreateLocDialog(false)}
      >
        <DialogTitle sx={{ p: 1.5, fontSize: 15, fontWeight: 600 }}>
          Create Location
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <AddLocations
              locationFlow='TL'
              handleClose={() => {
                setCreateLocDialog(false);
                GetAgencyLocationsHandler();
              }}
            />
          </DialogContentText>
        </DialogContent>
      </Dialog>

      {isNewUI ? (
        <Box
          sx={{
            // bgcolor: '#f5f6f8',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            p: { xs: 1, sm: 2 }
          }}
        >
          <Paper
            elevation={0}
            sx={{
              width: '100%',
              maxWidth: 920,
              borderRadius: 2.5,
              border: '0.5px solid',
              borderColor: 'divider',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {formHeader}
            <Box sx={{ p: { xs: 2, sm: 2.5 } }}>{formBody}</Box>
            {formFooter}
          </Paper>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            height: '90vh',
            maxWidth: '920px',
            margin: '0 auto',
            bgcolor: '#f5f6f8'
          }}
        >
          <Paper
            elevation={0}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              overflow: 'hidden',
              m: { xs: 1, sm: 2 },
              borderRadius: 2.5,
              border: '0.5px solid',
              borderColor: 'divider'
            }}
          >
            {formHeader}

            {/* Only this section scrolls */}
            <Box sx={{ flex: 1, overflowY: 'auto', p: { xs: 2, sm: 2.5 } }}>
              {formBody}
            </Box>

            {formFooter}
          </Paper>
        </Box>
      )}
    </>
  );
}
